import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, Check, Heart, UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { apiClient } from "../lib/apiClient";


const ICON_MAP = {
  prayer_added: Heart,
  prayer_answered: CheckCircle2,
  added_to_group: UserPlus,
};

const COLOR_MAP = {
  prayer_added: "text-sage",
  prayer_answered: "text-gold-dark",
  added_to_group: "text-blue-500",
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiClient.get("notificationsUnreadCount");
      setUnreadCount(res.data.count);
    } catch {
      // silent — notifications are non-critical
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const togglePanel = async () => {
    if (!open) {
      setLoading(true);
      try {
        const res = await apiClient.get("notifications");
        setNotifications(res.data);
      } catch {
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  const markAllRead = async () => {
    try {
      await apiClient.patch("notificationsMarkRead", {});
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      try {
        await apiClient.patch("notificationsMarkRead", { notification_ids: [notification.id] });
        setNotifications((prev) => prev.map((n) => n.id === notification.id ? { ...n, is_read: true } : n));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // silent
      }
    }
    if (notification.link) {
      window.location.href = notification.link;
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative h-9 w-9 p-0"
        onClick={togglePanel}
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? "text-sage" : "text-stone-dark/50"}`} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white border border-stone rounded-xl shadow-lg z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone">
            <h3 className="font-serif font-semibold text-stone-dark text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-sage hover:text-sage-dark transition-colors"
                data-testid="mark-all-read-btn"
              >
                <Check className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-stone-dark/40 text-sm">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-stone-dark/15 mx-auto mb-2" />
                <p className="text-sm text-stone-dark/40">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = ICON_MAP[n.type] || Bell;
                const iconColor = COLOR_MAP[n.type] || "text-stone-dark/50";
                return (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-stone/30 transition-colors border-b border-stone/50 last:border-0 ${
                      !n.is_read ? "bg-sage/5" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${!n.is_read ? "bg-sage/10" : "bg-stone/30"}`}>
                      <Icon className={`w-4 h-4 ${iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.is_read ? "text-stone-dark font-medium" : "text-stone-dark/70"}`}>
                        {n.message}
                      </p>
                      <p className="text-[10px] text-stone-dark/40 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <div className="w-2 h-2 rounded-full bg-sage shrink-0 mt-2" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
